import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { DisclosureStepList } from "@/components/flexkit/disclosure-step/DisclosureStep";
import { TrackLocations } from "@/hooks/use-segment";
import { useMemo, useState, type FC } from "react";
import { CreateWebhookForm } from "@/components/webhooks/new-webhook-configuration-dialog/CreateWebhookForm";
import { WebhookDetailsConfigurationStep } from "@/components/webhooks/new-webhook-configuration-dialog/WebhookDetailsStep";
import { useGetWebhook } from "@/queries/webhook-queries";
import { WebhookProvider } from "gitpod-next-api/gitpod/v1/webhook_pb";

type NewWebhookConfigurationDialogProps = {
    open: boolean;
    onClose: () => void;
};

export const NewWebhookConfigurationDialog: FC<NewWebhookConfigurationDialogProps> = ({ open, onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [step1Completed, setStep1Completed] = useState(false);
    const [createdWebhookId, setCreatedWebhookId] = useState<string | null>(null);

    const { data: webhook } = useGetWebhook(createdWebhookId ?? undefined);

    const providerName = useMemo(() => {
        switch (webhook?.spec?.provider) {
            case WebhookProvider.GITHUB:
                return "GitHub";
            case WebhookProvider.GITLAB:
                return "GitLab";
            default:
                return "your SCM provider";
        }
    }, [webhook?.spec?.provider]);

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setCurrentStep(0);
            setStep1Completed(false);
            setCreatedWebhookId(null);
            onClose();
        }
    };

    const handleWebhookCreated = (webhookId: string) => {
        setCreatedWebhookId(webhookId);
        setStep1Completed(true);
        setCurrentStep(1);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="overflow-hidden p-0"
                data-testid="new-webhook-configuration-dialog"
                data-track-location={TrackLocations.CreateWebhookDialog}
            >
                <DialogHeader className="mb-3 p-6 pb-0">
                    <DialogTitle>Configure new webhook</DialogTitle>
                    <DialogDescription>
                        Set up a webhook to connect Ona with your GitHub or GitLab repositories
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="select-none p-0 pb-6 pl-3 pr-6">
                    <DisclosureStepList currentStep={currentStep} onStepChange={setCurrentStep} canGoBack={false}>
                        <DisclosureStepList.Item
                            title="Webhook details"
                            description="Describe where this webhook lives and what it's used for."
                            completed={step1Completed}
                        >
                            <CreateWebhookForm onCancel={onClose} onSuccess={handleWebhookCreated} />
                        </DisclosureStepList.Item>
                        <DisclosureStepList.Item
                            title={`Set up the webhook in ${providerName}`}
                            description={`Create a webhook in ${providerName}, then copy the values below into the webhook settings.`}
                        >
                            {webhook ? (
                                <WebhookDetailsConfigurationStep webhook={webhook} onClose={onClose} />
                            ) : (
                                <div className="pb-4">
                                    <p className="text-sm text-content-secondary">Loading webhook details...</p>
                                </div>
                            )}
                        </DisclosureStepList.Item>
                    </DisclosureStepList>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
};
